import { useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, LabelList,
} from "recharts";
import type { UserRowV2 } from "./sql";
import { STAGES, medianGapDays } from "./helpers";
import { PRIMARY, MUTED, FG1 } from "../../lib/funnel/theme";
import { fmtN, fmtPct } from "../../lib/funnel/helpers";
import { SectionCardHeader } from "../../components/ChartCard";
import type { FilterPillSpec } from "../../components/FilterPills";

type Row = {
  label: string;
  fromCount: number;
  toCount: number;
  conversion: number;
  medianDays: number | null;
};

type LabelProps = {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  height?: string | number;
  value?: unknown;
};

const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

export function StepDropoff({
  users,
  pagePills,
}: {
  users: UserRowV2[];
  pagePills?: FilterPillSpec[];
}) {
  const rows: Row[] = useMemo(() => {
    const transitions: { fromLabel: string; from: (u: UserRowV2) => string | null; to: (u: UserRowV2) => string | null; toLabel: string }[] = [
      { fromLabel: "Signed up", from: (u) => u.created_at_utc, toLabel: STAGES[0]?.label ?? "", to: STAGES[0]?.dateOf ?? (() => null) },
    ];
    for (let i = 0; i < STAGES.length - 1; i++) {
      const a = STAGES[i];
      const b = STAGES[i + 1];
      if (!a || !b) continue;
      transitions.push({ fromLabel: a.label, from: a.dateOf, toLabel: b.label, to: b.dateOf });
    }
    return transitions.map((t) => {
      const fromUsers = users.filter((u) => t.from(u) != null);
      const toUsers = fromUsers.filter((u) => t.to(u) != null);
      const median = medianGapDays(toUsers, t.from, t.to);
      return {
        label: `${t.fromLabel} → ${t.toLabel}`,
        fromCount: fromUsers.length,
        toCount: toUsers.length,
        conversion: fromUsers.length === 0 ? 0 : (toUsers.length / fromUsers.length) * 100,
        medianDays: median,
      };
    });
  }, [users]);

  const renderLabel = (props: LabelProps) => {
    const x = num(props.x), y = num(props.y), w = num(props.width), h = num(props.height);
    if (x == null || y == null || w == null || h == null) return null;
    const idx = Math.round(y / h);
    const r = rows[idx];
    if (!r) return null;
    const txt = r.medianDays == null
      ? `${r.conversion.toFixed(1)}%`
      : `${r.conversion.toFixed(1)}%  ·  median ${r.medianDays}d`;
    return (
      <text x={x + w + 8} y={y + h / 2 + 4} fontSize={11} fontWeight={600} fill={FG1}>
        {txt}
      </text>
    );
  };

  return (
    <section className="card">
      <SectionCardHeader
        title="Step-to-step conversion and lag"
        subtitle="For each transition, the bar is the % of users who reached the FROM step who also reached the next step (any time later). Median days is the gap between the two timestamps, computed only over users who hit both."
        filterPills={pagePills}
      />
      <div className="card-body">
        <ResponsiveContainer width="100%" height={Math.max(160, rows.length * 36)}>
          <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 180, bottom: 4, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" horizontal={false} />
            <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: MUTED }} />
            <YAxis dataKey="label" type="category" tick={{ fontSize: 11, fill: FG1 }} width={210} interval={0} />
            <Tooltip
              cursor={{ fill: "rgba(0,0,0,0.03)" }}
              formatter={(_v, _n, item) => {
                const r = item?.payload as Row | undefined;
                if (!r) return ["", ""] as [string, string];
                const lag = r.medianDays == null ? "no overlap" : `${r.medianDays}d median lag`;
                return [
                  `${fmtPct(r.toCount, r.fromCount)} (${fmtN(r.toCount)} of ${fmtN(r.fromCount)}) · ${lag}`,
                  "Conversion",
                ] as [string, string];
              }}
            />
            <Bar dataKey="conversion" fill={PRIMARY} radius={[0, 3, 3, 0]} isAnimationActive={false}>
              <LabelList dataKey="conversion" content={renderLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
